import React, {
  forwardRef,
  ReactNode,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";

import { faLock } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { bem } from "@react-md/utils";
import Select, {
  DefaultOptionType,
  RefSelectProps,
  SelectProps,
  SelectValue,
} from "antd/es/select";
import Tooltip from "antd/es/tooltip";
import cn from "classnames";

import { isDefined } from "@mapmycustomers/shared/util/assert";

import { useConfigProvider } from "../../../ConfigProvider";
import ListWithOverflowItem from "../../../ListWithOverflowItem";
import Footer from "../Footer";
import InlineLabeled, { InlineLabeledProps } from "../InlineLabeled";

import Tag from "./Tag";

export const messages = {
  "ui.inlineInput.moreLabel": "+ {count} more",
  "ui.inlineInput.selectField.unknown": "Unknown",
};

export interface InlineSelectFieldProps<T extends SelectValue = SelectValue>
  extends Omit<SelectProps<T>, "className" | "ref">, Omit<InlineLabeledProps, "children"> {
  caption?: ReactNode;
  editing?: boolean;
  error?: ReactNode;
  onEditingChange?: (editing: boolean) => void;
  prefixIcon?: ReactNode;
  showFooterButtons?: boolean;
  valueRender?: (value: null | T | undefined) => ReactNode;
}

const block = bem("mmc-inline-select-field");
const InlineSelectField = <T extends SelectValue = SelectValue>(
  {
    caption,
    className,
    disabled,
    editing,
    label,
    labelClassName,
    labelPosition = "side",
    onChange,
    onEditingChange,
    options,
    placeholder,
    prefixIcon,
    required,
    showFooterButtons = true,
    tagRender,
    value,
    valueRender,
    ...selectProps
  }: InlineSelectFieldProps<T>,
  ref: React.Ref<InlineSelectFieldComponent>
) => {
  const configProvider = useConfigProvider();

  // TODO: consider moving these to useRef
  const [inlineValue, setInlineValue] = useState<null | T | undefined>(value);
  const [inlineOption, setInlineOption] = useState<
    DefaultOptionType | DefaultOptionType[] | undefined
  >();

  const [internalEditing, setInternalEditing] = useState(false);
  const isEditingControlled = isDefined(editing);
  const isEditing = isEditingControlled ? editing : internalEditing;

  const requestEditingChange = useCallback(
    (next: boolean) => {
      onEditingChange?.(next);
      if (!isEditingControlled) {
        setInternalEditing(next);
      }
    },
    [isEditingControlled, onEditingChange]
  );

  useEffect(() => {
    if (isEditingControlled) {
      setInlineValue(value);
    }
  }, [isEditingControlled, value]);

  const selectorRef = useRef<RefSelectProps>(null);

  const handleChange = useCallback(
    (value: T, option: DefaultOptionType | DefaultOptionType[]) => {
      if (!showFooterButtons && onChange) {
        onChange(value, option);
        requestEditingChange(false);
      } else {
        setInlineValue(value);
        setInlineOption(option);
      }
    },
    [onChange, requestEditingChange, showFooterButtons]
  );

  const handleSave = useCallback(() => {
    if (inlineOption && inlineValue != null) {
      onChange?.(inlineValue, inlineOption);
      requestEditingChange(false);
    }
  }, [inlineOption, inlineValue, requestEditingChange, onChange]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLElement>) => {
      if (event.key === "Escape") {
        event.stopPropagation();
        requestEditingChange(false);
      } else if (event.key === "Enter") {
        event.stopPropagation();
        handleSave();
      }
    },
    [requestEditingChange, handleSave]
  );

  const inputRef = useRef<RefSelectProps>(null);
  const handleStartEditing = useCallback(() => {
    if (disabled) {
      return;
    }
    setInlineValue(value);
    requestEditingChange(true);
    // can't focus hidden field, need to wait until it will be displayed
    setTimeout(() => {
      inputRef.current?.focus();
    }, 0);
  }, [disabled, requestEditingChange, value]);

  const activityTypeOverflowRender = useCallback(
    (count: number) => {
      if (!Array.isArray(value)) {
        return null;
      }

      const tooltip = value.slice(value.length - count).map((item, index) => {
        const label =
          typeof item === "object"
            ? item.label
            : options?.find((option) => item === option.value)?.label;
        return <div key={index}>{label}</div>;
      });

      return (
        <Tooltip title={tooltip}>
          <Tag>{configProvider.formatMessage("ui.inlineInput.moreLabel", { count })}</Tag>
        </Tooltip>
      );
    },
    [value, configProvider, options]
  );

  const formattedValue = useMemo(() => {
    if (valueRender) {
      return valueRender(value);
    }

    if (typeof value === "object") {
      if (Array.isArray(value)) {
        return (
          <ListWithOverflowItem overflowRender={activityTypeOverflowRender}>
            {value.map((item, index) => {
              const label =
                typeof item === "object"
                  ? item.label
                  : options?.find((option) => item === option.value)?.label;
              return <Tag key={index}>{label}</Tag>;
            })}
          </ListWithOverflowItem>
        );
      } else {
        return value?.label ?? (value as ReactNode);
      }
    } else {
      return options?.find((option) => value === option.value)?.label ?? (value as ReactNode);
    }
  }, [activityTypeOverflowRender, options, value, valueRender]);

  const handleBlur = useCallback<NonNullable<InlineSelectFieldProps["onBlur"]>>(
    (e) => {
      if (isEditingControlled) {
        return;
      }
      // To prevent blur action when cancel button is clicked as we save text onblur
      if (e.relatedTarget && e.relatedTarget.classList.contains("mmc-js-skip-save-on-blur")) {
        e.preventDefault();
        requestEditingChange(false);
        return;
      }
      handleSave();
    },
    [isEditingControlled, requestEditingChange, handleSave]
  );

  useImperativeHandle(ref, () => ({
    blur: () => {
      selectorRef.current?.blur();
    },
    focus: () => {
      selectorRef.current?.focus();
    },
  }));

  const handleDropdownVisibilityChange = useCallback(
    (visible: boolean) => {
      if (!visible && !showFooterButtons) {
        // timeout needed to complete dropdown hiding animation gracefully
        setTimeout(() => requestEditingChange(false), 200);
      }
    },
    [requestEditingChange, showFooterButtons]
  );

  const content =
    isEditing && !disabled ? (
      <Select
        allowClear={false}
        autoFocus={!isEditingControlled}
        className={block("select")}
        defaultOpen={!isEditingControlled}
        disabled={disabled}
        onBlur={handleBlur}
        onChange={handleChange}
        onDropdownVisibleChange={handleDropdownVisibilityChange}
        onKeyDown={handleKeyDown}
        options={options}
        ref={selectorRef}
        size="large"
        value={inlineValue}
        {...selectProps}
      />
    ) : (
      <div className={block("value")} onClick={handleStartEditing}>
        {formattedValue ?? (
          <span className={block("placeholder")}>
            {placeholder ?? configProvider.formatMessage("ui.inlineInput.unknown")}
          </span>
        )}
      </div>
    );

  return (
    <div className={cn(block({ disabled: !!disabled }), className)}>
      {label ? (
        <InlineLabeled
          extra={disabled ? <FontAwesomeIcon className={block("lock")} icon={faLock} /> : undefined}
          label={
            <div className={block("label-item")}>
              {prefixIcon}
              {label}
            </div>
          }
          labelClassName={labelClassName}
          labelPosition={labelPosition}
          required={required}
        >
          {content}
        </InlineLabeled>
      ) : (
        content
      )}
      {showFooterButtons && (
        <Footer
          caption={caption}
          disabled={disabled}
          editing={isEditing}
          onCancel={() => requestEditingChange(false)}
          onSave={handleSave}
        />
      )}
    </div>
  );
};

export interface InlineSelectFieldComponent {
  blur(): void;
  focus: () => void;
}

export default forwardRef(InlineSelectField) as <T extends SelectValue = SelectValue>(
  props: InlineSelectFieldProps<T> & { ref?: React.Ref<InlineSelectFieldComponent> }
) => React.ReactElement;
